import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/lib/motion";

const LINKS = [
  { id: "about", label: "About" },
  { id: "experience", label: "Experience" },
  { id: "services", label: "Studio" },
  { id: "work", label: "Work" },
  { id: "contact", label: "Contact" },
];

const handleClick = (e, id) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const MENU_EXIT = { opacity: 0, transition: { duration: 0.3 } };

const MobileMenu = ({ open, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (e, id) => {
    onClose();
    setTimeout(() => handleClick(e, id), 50);
    e.preventDefault();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={MENU_EXIT}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[70] md:hidden flex flex-col"
          style={{ background: "rgba(6,11,20,0.97)", backdropFilter: "blur(12px)" }}
          data-testid="mobile-menu"
        >
          <div className="container-x flex items-center justify-between h-16">
            <img src="/dc-logo.png" alt="Digital Centaur" className="w-10 h-10 object-contain" />
            <button
              onClick={onClose}
              aria-label="Close menu"
              data-testid="mobile-menu-close"
              className="p-2 text-ink hover:text-laser transition-colors"
            >
              <X size={26} strokeWidth={1.5} />
            </button>
          </div>

          <nav className="container-x flex-1 flex flex-col justify-center gap-1" data-testid="mobile-menu-links">
            {LINKS.map((l, i) => (
              <motion.a
                key={l.id}
                href={`#${l.id}`}
                onClick={(e) => go(e, l.id)}
                data-testid={`mobile-nav-link-${l.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.08 + i * 0.06, ease: EASE }}
                className="group flex items-baseline gap-4 py-3 border-b border-stroke/60"
              >
                <span className="font-mono text-[11px] tracking-[0.28em] text-laser">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif font-light text-4xl text-ink group-hover:text-laser transition-colors">
                  {l.label}
                </span>
              </motion.a>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 + LINKS.length * 0.06, ease: EASE }}
            >
              <Link
                to="/blog"
                onClick={onClose}
                data-testid="mobile-nav-link-journal"
                className="group flex items-baseline gap-4 py-3"
              >
                <span className="font-mono text-[11px] tracking-[0.28em] text-laser">
                  {String(LINKS.length + 1).padStart(2, "0")}
                </span>
                <span className="font-serif font-light text-4xl text-ink group-hover:text-laser transition-colors">
                  Journal
                </span>
                <ArrowUpRight size={20} strokeWidth={1.5} className="text-steel self-center" />
              </Link>
            </motion.div>
          </nav>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="container-x pb-10 font-mono text-[10px] tracking-[0.28em] uppercase text-steel"
          >
            Dubai, UAE · GMT+4
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [nearBottom, setNearBottom] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      setNearBottom(window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 160);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.9, ease: EASE }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-obsidian/85 backdrop-blur-md border-b border-stroke" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="container-x flex items-center justify-between h-16 md:h-20">
          <a
            href="#top"
            onClick={(e) => handleClick(e, "top")}
            data-testid="nav-logo"
            title={nearBottom ? "Back to top" : undefined}
            className="flex items-center group"
          >
            <span className={`inline-flex items-center justify-center transition-transform group-hover:rotate-[6deg] ${nearBottom ? "logo-pulse" : ""}`}>
              <img
                src="/dc-logo.png"
                alt="Digital Centaur"
                width="80"
                height="80"
                className="w-10 h-10 md:w-11 md:h-11 object-contain"
              />
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
            {LINKS.map((l, i) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={(e) => handleClick(e, l.id)}
                data-testid={`nav-link-${l.id}`}
                className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-steel hover:text-ink transition-colors"
              >
                <span className="text-laser/80">{String(i + 1).padStart(2, "0")}</span>
                {l.label}
              </a>
            ))}
            <Link
              to="/blog"
              data-testid="nav-link-journal"
              className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-steel hover:text-ink transition-colors"
            >
              <span className="text-laser/80">{String(LINKS.length + 1).padStart(2, "0")}</span>
              Journal
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <a
              href="#contact"
              onClick={(e) => handleClick(e, "contact")}
              data-testid="nav-cta"
              className="btn-sweep border border-stroke px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase text-ink"
            >
              Let&rsquo;s Talk
            </a>
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
              className="md:hidden p-2 text-ink hover:text-laser transition-colors"
            >
              <Menu size={24} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
